
import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { DefaultConfiguration } from '@models/document/default-configuration.const';
import { PermissionConfiguration } from '@models/document/permission-configuration.const';
import { InOutType } from '@models/generic/in-out-type.enum';

import {
  Article,
  ArticleOptions,
  ArticleString,
} from '@models/permissions/permission-type.enum';

import { PermissionDto } from '@models/permissions/permission.dto';
import { DocumentDto } from '@models/protocols/document.dto';
import { DelegateDto } from '@models/users/delegate.dto';

import { DocumentService } from '@services/document.service';
import { LoadingService } from '@services/loading.service';
import { MessageService } from '@services/message.service';
import { PermissionService } from '@services/permission.service';
import { ProtocolService } from '@services/protocol.service';

import { camelize } from '@utils/camelize.function';

import {
  DynamicDialogConfig,
  DynamicDialogRef,
} from 'primeng/dynamicdialog';

import { catchError } from 'rxjs';

@Component({
  selector: 'ebs-permission',
  templateUrl: './permission.component.html',
  styleUrls: ['./permission.component.scss'],
})
export class PermissionDialogComponent implements OnInit {

  public formGroup: FormGroup;

  public permission: PermissionDto = {
    article: Article.Art27,
    createdDate: new Date(),
    endTime: new Date(),
    isDaily: false,
    permissionDate: new Date(),
    startTime: new Date(),
    year: new Date().getFullYear(),
    totalTime: 6.5,
  };

  public minDate = new Date(
    Date.now() - 1000 * 60 * 60 * 24,
  );

  public options = {
    plugins: {},
  };

  public selectedDelegate!: DelegateDto;

  public delegates: DelegateDto[] = [];

  public artOptions = ArticleOptions;

  public artOptionStrings = ArticleString;

  public saving = false;

  constructor(
    private ref: DynamicDialogRef,
    private config: DynamicDialogConfig,
    private messageService: MessageService,
    private protocolService: ProtocolService,
    private documentService: DocumentService,
    private permissionService: PermissionService,
    private loader: LoadingService,
  ) {

    this.formGroup = new FormGroup({

      isDaily: new FormControl<boolean>(
        false,
        Validators.required,
      ),

      article: new FormControl<Article>(
        Article.Art27,
        Validators.required,
      ),

      delegate: new FormControl<DelegateDto | null>(
        null,
        Validators.required,
      ),

      createdDate: new FormControl<Date>(
        new Date(),
        Validators.required,
      ),

      permissionDate: new FormControl<Date>(
        new Date(),
        Validators.required,
      ),

      startTime: new FormControl<Date>(
        new Date(),
        Validators.required,
      ),

      endTime: new FormControl<Date>(
        new Date(),
        Validators.required,
      ),

      revokeDate: new FormControl<Date>(
        new Date(),
        Validators.required,
      ),

    });

    this.delegates = config.data?.delegates ?? [];
  }

  public ngOnInit(): void {}

  public cancel(): void {
    if (this.saving) {
      return;
    }

    this.ref.close(false);
  }

  public async confirm(): Promise<void> {

    if (this.saving || this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    if (!this.selectedDelegate) {
      this.messageService.error(
        'Nuovo permesso',
        'Selezionare un delegato',
      );
      return;
    }

    if (!this.permission.isDaily) {

      const startMinutes =
        this.permission.startTime.getHours() * 60 +
        this.permission.startTime.getMinutes();

      const endMinutes =
        this.permission.endTime.getHours() * 60 +
        this.permission.endTime.getMinutes();

      if (endMinutes <= startMinutes) {
        this.messageService.error(
          'Nuovo permesso',
          'L’ora di fine deve essere successiva all’ora di inizio',
        );
        return;
      }

      this.permission.totalTime =
        (endMinutes - startMinutes) / 60;
    }

    this.permission.delegate = this.selectedDelegate;

    this.saving = true;
    this.loader.show();

    try {

      const values = {

        generationDate: camelize(
          new Date().toLocaleDateString('it-IT', {
            day: '2-digit',
            month: 'long',
            year: 'numeric',
            weekday: 'long',
          }),
        ),

        name:
          `${this.permission.delegate?.user?.role} ` +
          `${this.permission.delegate?.user?.name} ` +
          `${this.permission.delegate?.user?.lastName}`,

        registerNumber:
          this.permission.delegate?.user?.registerNumber,

        permissionDate: camelize(
          this.permission.permissionDate.toLocaleDateString(
            'it-IT',
            {
              day: '2-digit',
              month: 'long',
              year: 'numeric',
              weekday: 'long',
            },
          ),
        ),

        article:
          `ex. art. ${this.permission.article} rif. CCNL TU 23/07/1976`,

        validFor: this.permission.isDaily
          ? "l'intera giornata indicata"
          : `l'orario compreso tra le ore ` +
            `${this.permission.startTime.getHours().toString().padStart(2, '0')}:` +
            `${this.permission.startTime.getMinutes().toString().padStart(2, '0')} ` +
            `e le ore ` +
            `${this.permission.endTime.getHours().toString().padStart(2, '0')}:` +
            `${this.permission.endTime.getMinutes().toString().padStart(2, '0')}`,
      };

      const delegationBlob =
        await this.documentService.writePdfFromUrl(
          'assets/documents/permission.pdf',
          PermissionConfiguration,
          values,
        );

      const firstUpload = {
        name:
          `FAISA-CISAL PISTOIA - Richiesta Permesso Sindacale Retribuito al ` +
          `${this.permission.delegate?.user?.role} ` +
          `${this.permission.delegate?.user?.name} ` +
          `${this.permission.delegate?.user?.lastName} ` +
          `${this.permission.delegate?.user?.registerNumber}.pdf`,
        hasProtocol: false,
        isSigned: false,
        mimeType: 'application/pdf',
      };

      this.documentService
        .uploadFile(firstUpload, delegationBlob)
        .subscribe({

          next: (firstReturn: DocumentDto) => {

            this.protocolService
              .createProtocol([
                {
                  document: firstReturn.docId!,
                  type: InOutType.OUT,
                },
              ])
              .subscribe({

                next: async (result) => {

                  try {

                    const blob =
                      await this.documentService.writePdfFromBuffer(
                        delegationBlob,
                        DefaultConfiguration,
                        {
                          protocol:
                            `Protocollo N. ${result!.protocolId} del ` +
                            new Date().toLocaleDateString('it-IT', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                            }),

                          orgName:
                            'Segreteria Provinciale FAISA-CISAL di Pistoia',

                          type:
                            `${result!.type == InOutType.IN ? 'E' : 'U'}`,
                        },
                      );

                    this.permission.permissionProtocol = result;

                    this.permissionService
                      .createPermission([this.permission])
                      .pipe(
                        catchError((err) => {
                          this.handleSaveError();
                          throw err;
                        }),
                      )
                      .subscribe({

                        next: () => {

                          this.documentService
                            .uploadFile(result.document!, blob)
                            .subscribe({

                              next: (res) => {

                                this.documentService
                                  .downloadFile(res)
                                  .subscribe({

                                    next: (blobResponse: any) => {

                                      const downloadLink =
                                        document.createElement('a');

                                      const objectUrl =
                                        window.URL.createObjectURL(
                                          blobResponse,
                                        );

                                      downloadLink.href = objectUrl;

                                      if (firstReturn.name) {
                                        downloadLink.setAttribute(
                                          'download',
                                          firstReturn.name,
                                        );
                                      }

                                      document.body.appendChild(
                                        downloadLink,
                                      );

                                      downloadLink.click();

                                      document.body.removeChild(
                                        downloadLink,
                                      );

                                      window.URL.revokeObjectURL(
                                        objectUrl,
                                      );

                                      this.loader.hide();
                                      this.saving = false;

                                      this.messageService.success(
                                        'Nuovo permesso',
                                        'Inserimento avvenuto con successo',
                                      );

                                      this.ref.close(true);
                                    },

                                    error: () => {
                                      this.handleSaveError();
                                    },
                                  });
                              },

                              error: () => {
                                this.handleSaveError();
                              },
                            });
                        },

                        error: () => {
                          // Gestito dal catchError.
                        },
                      });

                  } catch {
                    this.handleSaveError();
                  }
                },

                error: () => {
                  this.handleSaveError();
                },
              });
          },

          error: () => {
            this.handleSaveError();
          },
        });

    } catch {
      this.handleSaveError();
    }
  }

  private handleSaveError(): void {

    this.loader.hide();
    this.saving = false;

    this.messageService.error(
      'Nuovo permesso',
      "Errore durante l'inserimento, riprovare",
    );
  }
}
